import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  TextField,
  InputLabel,
  MenuItem,
  Select,
} from "@mui/material";
import { Alert } from "@mui/material";
import { Footer } from "../components/Footer";
import { policyLookup, PolicyLookupError } from "../utils/policyLookup";
import {
  isValidSouthAfricanId,
  sanitizePolicyNumber,
  isValidPolicyNumber,
} from "../utils/idValidation";

export function IdentifyPolicy({
  handleNext,
  handleBack,
  activeStep,
  isStepOptional,
  steps,
  totalFormData,
  handleDataSubmitted,
}: {
  handleNext: () => void;
  handleBack: () => void;
  activeStep: number;
  isStepOptional: (step: number) => boolean;
  steps: string[];
  totalFormData: Record<string, any>;
  handleDataSubmitted: (data: Record<string, any>, step: string) => void;
}) {
  const [whoIsSubmitting, setWhoIsSubmitting] = useState<string>("");
  const [brokerNumber, setBrokerNumber] = useState<string>("");
  const [policyNumber, setPolicyNumber] = useState<string>("");
  const [idNumber, setIdNumber] = useState<string>("");
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [lookupError, setLookupError] = useState<string>("");

  useEffect(() => {
    if (totalFormData["identify-policy"]) {
      const prev = totalFormData["identify-policy"];
      setWhoIsSubmitting(prev.whoIsSubmitting || "");
      setBrokerNumber(prev.brokerNumber || "");
      setPolicyNumber(prev.policyNumber || "");
      setIdNumber(prev.idNumber || "");
    }
  }, [totalFormData]);

  const validate = () => {
    const errors: Record<string, string> = {};
    const policy = sanitizePolicyNumber(policyNumber);
    const id = (idNumber || "").trim();
    const broker = (brokerNumber || "").trim();

    if (!whoIsSubmitting) {
      errors["who-is-submitting"] =
        "Please select who is submitting this claim";
    }

    if (!policy) {
      errors["policy-number"] = "Policy number is required";
    }

    if (!id) {
      errors["id-number"] = "South African ID number is required";
    }

    if (policy && !isValidPolicyNumber(policy)) {
      errors["policy-number"] =
        "Policy may contain only letters, numbers and hyphens";
    }

    if (id) {
      if (!/^[0-9]{13}$/.test(id)) {
        errors["id-number"] = "ID must be 13 numeric digits";
      } else if (!isValidSouthAfricanId(id)) {
        errors["id-number"] = "Invalid South African ID number";
      }
    }

    if (whoIsSubmitting === "Broker" && !broker) {
      errors["broker-number"] = "Broker number is required";
    }

    return errors;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLookupError("");
    const errors = validate();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    setIsLoading(true);
    try {
      if (!whoIsSubmitting) {
        setFormErrors({
          "who-is-submitting": "Please select who is submitting this claim",
        });
        setIsLoading(false);
        return;
      }
      const policy = sanitizePolicyNumber(policyNumber);
      const id = (idNumber || "").trim();
      const broker = (brokerNumber || "").trim();
      if (!policy || !id) {
        setFormErrors({
          "policy-number": !policy ? "Policy number is required" : "",
          "id-number": !id ? "South African ID number is required" : "",
        });
        setIsLoading(false);
        return;
      }
      if (whoIsSubmitting === "Broker" && !broker) {
        setFormErrors({
          "broker-number": "Broker number is required",
        });
        setIsLoading(false);
        return;
      }
      const result = await policyLookup(id, policy);
      const payload = {
        policyNumber: policy,
        idNumber: id,
        whoIsSubmitting: whoIsSubmitting,
        brokerNumber: whoIsSubmitting === "Broker" ? brokerNumber : "",
        policyId: result.policyId,
        policyholder: result.policyholder,
        dependents: result.dependents,
        policyholderRiskId: result.policyholderRiskId,
        resolvedPolicyNumber: result.resolvedPolicyNumber,
        resolvedIdNumber: result.resolvedIdNumber,
        resolvedAddressLine1: result.resolvedAddressLine1,
        resolvedAddressLine2: result.resolvedAddressLine2,
        resolvedCity: result.resolvedCity,
        resolvedZipCode: result.resolvedZipCode,
        resolvedMobileNo: result.resolvedMobileNo,
        resolvedEmail: result.resolvedEmail,
      };
      handleDataSubmitted(payload, "identify-policy");
      handleNext();
    } catch (err) {
      const e = err as PolicyLookupError;
      let message = "Lookup failed. Please verify details or try again later.";
      if (e && e.code === "NOT_FOUND")
        message =
          "Policy not found. Please confirm details or contact claims@sirago.co.za.";
      else if (e && e.code === "INACTIVE")
        message =
          "This policy is inactive. Please contact claims@sirago.co.za for assistance.";
      else if (e && e.code === "MISMATCH_ID")
        message =
          "ID number does not match this policy. Please confirm details or contact claims@sirago.co.za.";
      setLookupError(message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <h4 style={{ borderBottom: "1px solid #ccc", margin: "1em 0" }}>
        Identify policy
      </h4>
      {lookupError && (
        <Box sx={{ m: { xs: 0, sm: 1 }, width: { xs: 300, sm: 600 } }}>
          <Alert severity="error">{lookupError}</Alert>
        </Box>
      )}
      <form onSubmit={handleSubmit} style={{ paddingBottom: "5em" }}>
        <FormControl
          sx={{
            display: "block",
            m: { xs: 0, sm: 1 },
            mb: { xs: 1, sm: 1 },
            minWidth: { xs: 300, sm: 600 },
            // minWidth: { xs: "100%", sm: "auto" },
          }}
          error={!!formErrors["who-is-submitting"]}
        >
          <InputLabel id="who-is-submitting-label">
            Who is submitting?
          </InputLabel>
          <Select
            labelId="who-is-submitting-label"
            id="who-is-submitting"
            value={whoIsSubmitting}
            label="Who is submitting?"
            sx={{ minWidth: { xs: 300, sm: 600 } }}
            onChange={(e) => setWhoIsSubmitting(e.target.value as string)}
            required
          >
            <MenuItem value="Policy holder">Policy holder</MenuItem>
            <MenuItem value="Broker">Broker</MenuItem>
          </Select>
          <FormHelperText>
            {formErrors["who-is-submitting"] ||
              "Select who is submitting this claim."}
          </FormHelperText>
        </FormControl>

        {whoIsSubmitting && (
          <>
            {whoIsSubmitting === "Broker" && (
              <FormControl
                sx={{
                  m: { xs: 0, sm: 1 },
                  mb: { xs: 1, sm: 1 },
                  width: { xs: 300, sm: 600 },
                  minWidth: { xs: "100%", sm: "auto" },
                }}
                error={!!formErrors["broker-number"]}
              >
                <TextField
                  id="broker-number"
                  name="broker-number"
                  label="Broker number"
                  value={brokerNumber}
                  onChange={(e) => setBrokerNumber(e.target.value)}
                  required
                />
                <FormHelperText>
                  {formErrors["broker-number"] ||
                    "Enter your registered broker number."}
                </FormHelperText>
              </FormControl>
            )}

            <FormControl
              sx={{
                m: { xs: 0, sm: 1 },
                mb: { xs: 1, sm: 1 },
                width: { xs: 300, sm: 600 },
                minWidth: { xs: "100%", sm: "auto" },
              }}
              error={!!formErrors["policy-number"]}
            >
              <TextField
                id="policy-number"
                name="policy-number"
                label="Policy Number"
                value={policyNumber}
                onChange={(e) => setPolicyNumber(e.target.value)}
                required
              />
              {formErrors["policy-number"] && (
                <FormHelperText>{formErrors["policy-number"]}</FormHelperText>
              )}
            </FormControl>

            <FormControl
              sx={{
                m: { xs: 0, sm: 1 },
                mb: { xs: 1, sm: 1 },
                width: { xs: 300, sm: 600 },
                minWidth: { xs: "100%", sm: "auto" },
              }}
              error={!!formErrors["id-number"]}
            >
              <TextField
                id="id-number"
                name="id-number"
                label="South African ID Number"
                value={idNumber}
                onChange={(e) => setIdNumber(e.target.value)}
                required
              />
              {formErrors["id-number"] && (
                <FormHelperText>{formErrors["id-number"]}</FormHelperText>
              )}
            </FormControl>
          </>
        )}

        <Box sx={{ display: "flex", flexDirection: "row", py: 2, mb: "10em" }}>
          <Button
            color="inherit"
            disabled={activeStep === 0}
            onClick={handleBack}
            sx={{ mr: 1, fontSize: "1.2em" }}
          >
            Back
          </Button>
          <Box sx={{ flex: "1 1 auto" }} />
          <Button type="submit" sx={{ fontSize: "1.2em" }} disabled={isLoading}>
            {isLoading ? "Looking up..." : "Next"}
          </Button>
        </Box>
      </form>
      <Footer />
    </>
  );
}
